import React, { useEffect, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  Image as ImageIcon,
  Wand2,
  Download,
  RefreshCw,
  X,
  Loader2,
  BookmarkCheck,
  Save,
  Trash2,
} from "lucide-react";

/**
 * StaffCoverButton (v1.16, June 2026)
 *
 * Pulsante "Genera copertina" usato dall'agency staff. Apre un dialog che:
 *   1. Carica lo screenshot del frame video del cliente (= soggetti)
 *   2. Carica un'immagine di riferimento stilistico (= stile da clonare)
 *      Se il cliente ha già un riferimento salvato, viene auto-caricato.
 *   3. Scrivi un testo libero (l'AI gestisce layout / line breaks / accenti)
 *   4. Genera UNA copertina che replica lo stile del riferimento applicato
 *      ai nuovi soggetti, con il nuovo testo.
 *   5. Dopo la generazione, se il riferimento è "fresco" (non salvato),
 *      mostra prompt giallo "Vuoi salvarlo come stile predefinito?"
 *   6. Scarica / Rigenera.
 *
 * Nessuna quota giornaliera (uso interno illimitato).
 */

export default function StaffCoverButton({ clientId, clientName }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [refFile, setRefFile] = useState(null);
  const [refPreviewSrc, setRefPreviewSrc] = useState(null);
  const [refIsSaved, setRefIsSaved] = useState(false);
  const [savingRef, setSavingRef] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [title, setTitle] = useState("");
  const [result, setResult] = useState(null); // { dataUrl }
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const refInputRef = useRef(null);

  // Carica automaticamente il riferimento salvato per questo cliente
  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/staff/clients/${clientId}/cover/reference`
        );
        if (cancelled) return;
        if (data?.has_reference && data?.image_base64) {
          const mime = data.mime_type || "image/jpeg";
          const dataUrl = `data:${mime};base64,${data.image_base64}`;
          const binStr = atob(data.image_base64);
          const bytes = new Uint8Array(binStr.length);
          for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
          const ext = (mime.split("/")[1] || "jpg").replace("jpeg", "jpg");
          const f = new File([bytes], `ref-cliente.${ext}`, { type: mime });
          setRefFile(f);
          setRefPreviewSrc(dataUrl);
          setRefIsSaved(true);
        }
      } catch (e) {
        // 404 / network → ignora
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, clientId]);

  const reset = () => {
    setFile(null);
    setPreviewSrc(null);
    setRefFile(null);
    setRefPreviewSrc(null);
    setRefIsSaved(false);
    setShowSavePrompt(false);
    setTitle("");
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
    if (refInputRef.current) refInputRef.current.value = "";
  };

  const onClose = (v) => {
    if (!v) {
      setOpen(false);
      setTimeout(reset, 200);
    } else {
      setOpen(v);
    }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Carica un'immagine (JPG o PNG)");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 8 MB)");
      return;
    }
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => setPreviewSrc(reader.result);
    reader.readAsDataURL(f);
  };

  const pickRefFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Carica un'immagine (JPG o PNG) come riferimento");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Immagine di riferimento troppo grande (max 8 MB)");
      return;
    }
    setRefFile(f);
    setRefIsSaved(false);
    setShowSavePrompt(false);
    const reader = new FileReader();
    reader.onload = () => setRefPreviewSrc(reader.result);
    reader.readAsDataURL(f);
  };

  const clearRef = () => {
    setRefFile(null);
    setRefPreviewSrc(null);
    setRefIsSaved(false);
    setShowSavePrompt(false);
    if (refInputRef.current) refInputRef.current.value = "";
  };

  const saveRefToClient = async () => {
    if (!refFile) return;
    setSavingRef(true);
    try {
      const form = new FormData();
      form.append("file", refFile);
      await api.put(`/staff/clients/${clientId}/cover/reference`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRefIsSaved(true);
      setShowSavePrompt(false);
      toast.success("Riferimento salvato come stile predefinito ✓");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingRef(false);
    }
  };

  const deleteRefFromClient = async () => {
    setSavingRef(true);
    try {
      await api.delete(`/staff/clients/${clientId}/cover/reference`);
      clearRef();
      toast.success("Riferimento predefinito rimosso");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingRef(false);
    }
  };

  const buildForm = () => {
    const form = new FormData();
    form.append("file", file);
    form.append("reference", refFile);
    form.append("title", title.trim());
    return form;
  };

  const generate = async () => {
    if (!file) {
      toast.error("Seleziona prima uno screenshot");
      return;
    }
    if (!refFile) {
      toast.error("Carica un'immagine di riferimento stilistico");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(
        `/staff/clients/${clientId}/cover/generate`,
        buildForm(),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (!data?.image_base64) {
        toast.error("L'AI non ha prodotto l'immagine. Riprova fra poco.");
        return;
      }
      setResult({
        dataUrl: `data:${data.mime_type || "image/png"};base64,${data.image_base64}`,
      });
      toast.success("Copertina pronta.");
      if (refFile && !refIsSaved) {
        setShowSavePrompt(true);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result?.dataUrl) return;
    const a = document.createElement("a");
    a.href = result.dataUrl;
    const safeClient = (clientName || "cliente")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40);
    a.download = `copertina-${safeClient}-${new Date()
      .toISOString()
      .slice(0, 10)}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`prod-cover-btn-${clientId}`}
        title="Genera copertina AI per questo cliente"
        className="h-8 w-8 rounded-full bg-[var(--mp-primary)] text-white grid place-items-center hover:opacity-90 transition-opacity"
      >
        <Wand2 className="h-3.5 w-3.5" />
      </button>

      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          data-testid="staff-cover-dialog"
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>
              Genera copertina · {clientName || "Cliente"}
            </DialogTitle>
            <DialogDescription>
              L&apos;AI clona lo <strong>stile del riferimento</strong> sui
              soggetti dello screenshot, applicando il tuo testo. Più simile
              è il riferimento al risultato che vuoi, più la copertina sarà
              fedele.
            </DialogDescription>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4">
              {/* TITOLO LIBERO */}
              <div className="space-y-2">
                <label
                  htmlFor={`cover-title-${clientId}`}
                  className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]"
                >
                  Testo da scrivere sulla copertina (opzionale)
                </label>
                <textarea
                  id={`cover-title-${clientId}`}
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, 240))}
                  placeholder="Scrivi il testo come vuoi (anche più righe). L'AI lo disporrà seguendo la gerarchia visiva del riferimento."
                  maxLength={240}
                  rows={3}
                  data-testid="staff-cover-title-input"
                  className="w-full rounded-xl border border-[var(--mp-ink)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--mp-yellow)] resize-none"
                />
                <div className="text-[10px] text-[var(--mp-muted)]">
                  {title.length}/240 caratteri · L&apos;AI mantiene il testo
                  esatto (anche maiuscole/punteggiatura) e lo posiziona al
                  centro della copertina.
                </div>
              </div>

              {/* SCREENSHOT VIDEO */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] mb-2">
                  Screenshot del video (= soggetti della copertina)
                </div>
                <div className="rounded-2xl border-2 border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-6 grid place-items-center min-h-[160px]">
                  {previewSrc ? (
                    <img
                      src={previewSrc}
                      alt="Anteprima frame"
                      className="max-h-[280px] w-auto rounded-xl object-contain"
                    />
                  ) : (
                    <div className="text-center text-sm text-[var(--mp-muted)]">
                      Carica uno screenshot del frame video da usare come base
                    </div>
                  )}
                </div>
              </div>

              {/* RIFERIMENTO STILE (obbligatorio) */}
              <div className="rounded-2xl border border-[var(--mp-border)] bg-white/60 p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                        Riferimento stile (obbligatorio)
                      </div>
                      {refIsSaved && (
                        <span
                          data-testid="staff-cover-ref-saved-badge"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] text-[10px] font-bold"
                          title="Caricato automaticamente: è lo stile salvato per questo cliente"
                        >
                          <BookmarkCheck className="h-3 w-3" />
                          Salvato per questo cliente
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[var(--mp-muted)] mt-1 leading-snug">
                      La copertina che vuoi imitare il più possibile
                      (background, luci, colori, font, decorazioni…).
                      {refIsSaved ? (
                        <span>
                          {" "}Questo è lo <strong>stile predefinito</strong>
                          {" "}di <strong>{clientName || "questo cliente"}</strong>.
                          Per cambiarlo carica un nuovo file.
                        </span>
                      ) : (
                        <span>
                          {" "}Senza riferimento la generazione non parte.
                        </span>
                      )}
                    </div>
                  </div>
                  {refFile && !refIsSaved && (
                    <button
                      type="button"
                      onClick={clearRef}
                      data-testid="staff-cover-ref-clear-btn"
                      className="shrink-0 inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-[var(--mp-ink)] text-[11px] hover:bg-[var(--mp-dash-bg)]"
                      title="Togli il riferimento"
                    >
                      <X className="h-3 w-3" />
                      Togli
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    ref={refInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={pickRefFile}
                    className="hidden"
                    data-testid="staff-cover-ref-input"
                  />
                  <Button
                    onClick={() => refInputRef.current?.click()}
                    variant="outline"
                    type="button"
                    data-testid="staff-cover-ref-pick-btn"
                    className="rounded-full border-[var(--mp-ink)]"
                  >
                    <ImageIcon className="h-4 w-4 mr-2" />
                    {refFile ? "Cambia riferimento" : "Carica riferimento stile"}
                  </Button>
                  {refIsSaved && (
                    <Button
                      onClick={deleteRefFromClient}
                      disabled={savingRef}
                      variant="outline"
                      type="button"
                      data-testid="staff-cover-ref-delete-saved-btn"
                      className="rounded-full border-red-400 text-red-600 hover:bg-red-50"
                      title="Elimina lo stile predefinito dal server"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Elimina stile salvato
                    </Button>
                  )}
                  {refPreviewSrc && (
                    <img
                      src={refPreviewSrc}
                      alt="Riferimento stilistico"
                      data-testid="staff-cover-ref-preview"
                      className="max-h-[120px] w-auto rounded-lg object-contain border border-[var(--mp-border)]"
                    />
                  )}
                </div>
              </div>

              {/* AZIONI */}
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[var(--mp-border)]">
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={pickFile}
                  className="hidden"
                  data-testid="staff-cover-file-input"
                />
                <Button
                  onClick={() => inputRef.current?.click()}
                  variant="outline"
                  data-testid="staff-cover-pick-btn"
                  className="rounded-full border-[var(--mp-ink)]"
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  {file ? "Cambia screenshot" : "Scegli screenshot"}
                </Button>
                <Button
                  onClick={generate}
                  disabled={!file || !refFile || loading}
                  data-testid="staff-cover-generate-btn"
                  className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  {loading ? "L'AI sta lavorando…" : "Genera copertina"}
                </Button>
              </div>
              {loading && (
                <div className="text-[12px] text-[var(--mp-muted)] italic">
                  Ci vogliono ~20-40 secondi. Lascia la finestra aperta.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* PROMPT SALVATAGGIO REF */}
              {showSavePrompt && refFile && !refIsSaved && (
                <div
                  data-testid="staff-cover-save-ref-prompt"
                  className="rounded-2xl border-2 border-[var(--mp-yellow)] bg-[var(--mp-yellow)]/20 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
                >
                  <div className="text-sm flex-1 min-w-[200px]">
                    <strong>Vuoi salvare questo riferimento</strong> come stile
                    predefinito di <strong>{clientName || "questo cliente"}</strong>?
                    <div className="text-[11px] text-[var(--mp-muted)] mt-1">
                      Verrà ricaricato automaticamente alla prossima generazione.
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={saveRefToClient}
                      disabled={savingRef}
                      data-testid="staff-cover-save-ref-yes-btn"
                      className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
                    >
                      {savingRef ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Sì, salva
                    </Button>
                    <Button
                      onClick={() => setShowSavePrompt(false)}
                      disabled={savingRef}
                      variant="outline"
                      data-testid="staff-cover-save-ref-no-btn"
                      className="rounded-full border-[var(--mp-ink)]"
                    >
                      No, solo questa volta
                    </Button>
                  </div>
                </div>
              )}

              {/* RISULTATO */}
              <div className="rounded-2xl border border-[var(--mp-ink)] bg-[var(--mp-dash-bg)] p-3 grid place-items-center">
                <img
                  src={result.dataUrl}
                  alt="Copertina generata"
                  data-testid="staff-cover-result-image"
                  className="w-auto h-auto object-contain max-h-[480px] rounded-xl"
                />
              </div>

              {/* AZIONI RISULTATO */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={download}
                  data-testid="staff-cover-download-btn"
                  className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Scarica copertina
                </Button>
                <Button
                  onClick={() => {
                    setResult(null);
                    generate();
                  }}
                  disabled={loading}
                  variant="outline"
                  data-testid="staff-cover-regen-btn"
                  className="rounded-full border-[var(--mp-ink)]"
                  title="Rigenera con gli stessi input"
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                  />
                  Rigenera
                </Button>
                <Button
                  onClick={() => setResult(null)}
                  variant="outline"
                  data-testid="staff-cover-tweak-btn"
                  className="rounded-full border-[var(--mp-ink)]"
                  title="Torna alla schermata input per modificare titolo/screenshot/riferimento"
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  Modifica input
                </Button>
                <Button
                  onClick={() => onClose(false)}
                  variant="outline"
                  className="rounded-full border-[var(--mp-ink)]"
                >
                  <X className="h-4 w-4 mr-2" />
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
